/**
 * The app inside the Media Manager's „Writing & Updates“
 * (`apps/mediamanager/src/web/components/writing/`).
 *
 * The host lists the tools in its own sidebar and owns Hell/Dunkel, so the
 * framed app drops its sidebar and follows the host. The host opens
 * `/?embedded=1&theme=dark#/<view>` — `public/theme-init.js` takes the theme
 * before the first paint — and then sends `{ type: 'hkcm:embed', theme?, view? }`.
 * The app answers `{ type: 'hkcm:view', view }` when the page on screen changes
 * (a link in the Tutorial, the back button), so the host's sidebar marks it.
 *
 * The app runs on the host's sign-in (`contexts/HostAuthContext.tsx`): it asks
 * `{ type: 'hkcm:token-request', id, fresh }`, and the host answers
 * `{ type: 'hkcm:token', id, token }` with the access token its web app holds
 * for its own API, which this app's API accepts for the Media Manager's groups.
 *
 * Only the frame's parent is heard, and after its first message only from that
 * origin; every field is checked, and the answer goes to that origin alone.
 * Who may frame the app at all is the server's `frame-ancestors`.
 */

import type { Theme } from './hooks/useTheme'
import { isView, type View } from './navigation'

const HOST_MESSAGE = 'hkcm:embed'
const VIEW_MESSAGE = 'hkcm:view'
const TOKEN_REQUEST = 'hkcm:token-request'
const TOKEN_MESSAGE = 'hkcm:token'

/** How long a token request waits for the host; without an answer the app counts as signed out. */
export const TOKEN_TIMEOUT_MS = 15_000

/** Whether this window is the app in a frame whose host asked for the embedded shell. */
export function isEmbedded(win: Window): boolean {
    if (win.parent === win) return false
    return new URLSearchParams(win.location.search).get('embedded') === '1'
}

export interface HostRequest {
    theme?: Theme
    view?: View
}

/** What a host message asks for, field by field; null for anything that is not one. */
export function parseHostRequest(data: unknown): HostRequest | null {
    if (typeof data !== 'object' || data === null) return null
    const { type, theme, view } = data as Record<string, unknown>
    if (type !== HOST_MESSAGE) return null
    const request: HostRequest = {}
    if (theme === 'light' || theme === 'dark') request.theme = theme
    if (typeof view === 'string' && isView(view)) request.view = view
    return request
}

export interface TokenAnswer {
    id: number
    /** Null when the host has no sign-in to hand over. */
    token: string | null
}

/** The host's answer to a token request; null for anything that is not one. */
export function parseTokenAnswer(data: unknown): TokenAnswer | null {
    if (typeof data !== 'object' || data === null) return null
    const { type, id, token } = data as Record<string, unknown>
    if (type !== TOKEN_MESSAGE || typeof id !== 'number') return null
    return { id, token: typeof token === 'string' && token !== '' ? token : null }
}

/** Whom the host's token names, and until when it holds: for the shell and for renewal; the API checks it. */
export interface HostSession {
    token: string
    name: string
    username: string
    /** Milliseconds since the epoch. */
    expiresAt: number
}

/** A token whose expiry cannot be read counts for five minutes. */
const UNREADABLE_TOKEN_MS = 5 * 60 * 1000

export function sessionFromToken(token: string, now = Date.now()): HostSession {
    const claims = readClaims(token)
    const text = (value: unknown) => (typeof value === 'string' ? value : '')
    // An access token of version 1 names the account in upn or unique_name, not preferred_username.
    const username = text(claims.preferred_username) || text(claims.upn) || text(claims.unique_name) || text(claims.email)
    return {
        token,
        name: text(claims.name) || username,
        username,
        expiresAt: typeof claims.exp === 'number' ? claims.exp * 1000 : now + UNREADABLE_TOKEN_MS,
    }
}

function readClaims(token: string): Record<string, unknown> {
    try {
        const part = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/')
        const bytes = Uint8Array.from(atob(part.padEnd(Math.ceil(part.length / 4) * 4, '=')), (c) => c.charCodeAt(0))
        const claims: unknown = JSON.parse(new TextDecoder().decode(bytes))
        return typeof claims === 'object' && claims !== null ? (claims as Record<string, unknown>) : {}
    } catch {
        return {}
    }
}

export interface HostBridge {
    /** Takes one `message` event: the host's theme and page are applied, anything else is ignored. */
    receive: (event: Pick<MessageEvent, 'data' | 'origin' | 'source'>) => void
    /** Tells the host the page on screen; held back until the host has spoken. */
    report: (view: View) => void
    /**
     * Asks the host for its sign-in, a new one when `fresh`. Null when it has
     * none, or no answer came within `TOKEN_TIMEOUT_MS`.
     */
    requestToken: (fresh?: boolean) => Promise<string | null>
}

export function createHostBridge(win: Window): HostBridge {
    let origin: string | null = null
    let unsent: View | null = null
    let requests = 0
    const waiting = new Map<number, (token: string | null) => void>()

    const send = (view: View, to: string) => win.parent.postMessage({ type: VIEW_MESSAGE, view }, to)

    return {
        receive(event) {
            if (event.source !== win.parent) return
            if (origin !== null && event.origin !== origin) return
            const answer = parseTokenAnswer(event.data)
            if (answer) {
                waiting.get(answer.id)?.(answer.token)
                waiting.delete(answer.id)
                return
            }
            const request = parseHostRequest(event.data)
            if (!request) return
            origin = event.origin
            if (request.theme) win.document.documentElement.setAttribute('data-theme', request.theme)
            // useActiveView follows the hash, the way it follows the back button.
            if (request.view && win.location.hash !== `#/${request.view}`) win.location.hash = `#/${request.view}`
            if (unsent) {
                send(unsent, origin)
                unsent = null
            }
        },
        report(view) {
            if (origin === null) unsent = view
            else send(view, origin)
        },
        requestToken(fresh = false) {
            const id = ++requests
            return new Promise((resolve) => {
                waiting.set(id, resolve)
                win.setTimeout(() => {
                    if (waiting.delete(id)) resolve(null)
                }, TOKEN_TIMEOUT_MS)
                // The request carries nothing secret, so it may go out before
                // the host has spoken and its origin is known.
                win.parent.postMessage({ type: TOKEN_REQUEST, id, fresh }, origin ?? '*')
            })
        },
    }
}

/** This window's bridge to the Media Manager; null when the app runs on its own. */
export const host: HostBridge | null = isEmbedded(window) ? createHostBridge(window) : null
